
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ExpandedGroups } from './types';
import { useToast } from "@/hooks/use-toast";

interface SidebarStateContextType {
  expandedGroups: ExpandedGroups;
  toggleGroup: (group: keyof ExpandedGroups) => void;
  setActiveGroup: (group: keyof ExpandedGroups) => void;
}

// Create context with a meaningful default value
const SidebarStateContext = createContext<SidebarStateContextType | undefined>(undefined);

// LocalStorage key for persisting sidebar state
const SIDEBAR_STATE_KEY = 'sidebar-expanded-groups';

export function SidebarStateProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const [expandedGroups, setExpandedGroups] = useState<ExpandedGroups>(() => {
    // Try to get state from localStorage
    if (typeof window === 'undefined') {
      return {
        investors: false,
        resources: false,
        tools: false,
        training: false,
        account: false,
        company: false,
        roadmap: false
      };
    }
    
    try {
      const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
      return savedState ? JSON.parse(savedState) : {
        investors: false,
        resources: false,
        tools: false,
        training: false,
        account: false,
        company: false,
        roadmap: false
      };
    } catch (error) {
      console.error('Error loading sidebar state from localStorage:', error);
      return {
        investors: false,
        resources: false,
        tools: false,
        training: false,
        account: false,
        company: false,
        roadmap: false
      };
    }
  });

  // Save state to localStorage when it changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(expandedGroups));
      }
    } catch (error) {
      console.error('Error saving sidebar state to localStorage:', error);
    }
  }, [expandedGroups]);

  const toggleGroup = useCallback((group: keyof ExpandedGroups) => {
    // Use functional update to ensure we're working with the latest state
    setExpandedGroups(prev => {
      const updated = {
        ...prev,
        [group]: !prev[group]
      };
      return updated;
    });
  }, []);

  const setActiveGroup = useCallback((group: keyof ExpandedGroups) => {
    setExpandedGroups(prev => {
      // Only expand the clicked group if it's not already expanded
      if (prev[group]) {
        return prev; // Don't change anything if already expanded
      }
      
      return {
        ...prev,
        [group]: true
      };
    });
  }, []);

  const value = {
    expandedGroups,
    toggleGroup,
    setActiveGroup
  };

  return (
    <SidebarStateContext.Provider value={value}>
      {children}
    </SidebarStateContext.Provider>
  );
}

export function useSidebarState() {
  const context = useContext(SidebarStateContext);
  if (!context) {
    throw new Error('useSidebarState must be used within a SidebarStateProvider');
  }
  return context;
}
